import { debounce } from 'lodash-es';
import { sha256 } from '../../utils/helper';

const currencies = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: '$',
  AUD: '$',
  INR: '₹',
};

const countriesStates = {
  US: [{ name: 'Alabama', abbreviation: 'AL' }, { name: 'Alaska', abbreviation: 'AK' }, { name: 'Arizona', abbreviation: 'AZ' }, { name: 'Arkansas', abbreviation: 'AR' }, { name: 'California', abbreviation: 'CA' }, { name: 'Colorado', abbreviation: 'CO' }, { name: 'Connecticut', abbreviation: 'CT' }, { name: 'Delaware', abbreviation: 'DE' }, { name: 'Florida', abbreviation: 'FL' }, { name: 'Georgia', abbreviation: 'GA' }, { name: 'Hawaii', abbreviation: 'HI' }, { name: 'Idaho', abbreviation: 'ID' }, { name: 'Illinois', abbreviation: 'IL' }, { name: 'Indiana', abbreviation: 'IN' }, { name: 'Iowa', abbreviation: 'IA' }, { name: 'Kansas', abbreviation: 'KS' }, { name: 'Kentucky', abbreviation: 'KY' }, { name: 'Louisiana', abbreviation: 'LA' }, { name: 'Maine', abbreviation: 'ME' }, { name: 'Maryland', abbreviation: 'MD' }, { name: 'Massachusetts', abbreviation: 'MA' }, { name: 'Michigan', abbreviation: 'MI' }, { name: 'Minnesota', abbreviation: 'MN' }, { name: 'Mississippi', abbreviation: 'MS' }, { name: 'Missouri', abbreviation: 'MO' }, { name: 'Montana', abbreviation: 'MT' }, { name: 'Nebraska', abbreviation: 'NE' }, { name: 'Nevada', abbreviation: 'NV' }, { name: 'New Hampshire', abbreviation: 'NH' }, { name: 'New Jersey', abbreviation: 'NJ' }, { name: 'New Mexico', abbreviation: 'NM' }, { name: 'New York', abbreviation: 'NY' }, { name: 'North Carolina', abbreviation: 'NC' }, { name: 'North Dakota', abbreviation: 'ND' }, { name: 'Ohio', abbreviation: 'OH' }, { name: 'Oklahoma', abbreviation: 'OK' }, { name: 'Oregon', abbreviation: 'OR' }, { name: 'Pennsylvania', abbreviation: 'PA' }, { name: 'Rhode Island', abbreviation: 'RI' }, { name: 'South Carolina', abbreviation: 'SC' }, { name: 'South Dakota', abbreviation: 'SD' }, { name: 'Tennessee', abbreviation: 'TN' }, { name: 'Texas', abbreviation: 'TX' }, { name: 'Utah', abbreviation: 'UT' }, { name: 'Vermont', abbreviation: 'VT' }, { name: 'Virginia', abbreviation: 'VA' }, { name: 'Washington', abbreviation: 'WA' }, { name: 'West Virginia', abbreviation: 'WV' }, { name: 'Wisconsin', abbreviation: 'WI' }, { name: 'Wyoming', abbreviation: 'WY' }],
  CA: [{ name: 'Alberta', abbreviation: 'AB' }, { name: 'British Columbia', abbreviation: 'BC' }, { name: 'Manitoba', abbreviation: 'MB' }, { name: 'New Brunswick', abbreviation: 'NB' }, { name: 'Newfoundland and Labrador', abbreviation: 'NL' }, { name: 'Northwest Territories', abbreviation: 'NT' }, { name: 'Nova Scotia', abbreviation: 'NS' }, { name: 'Nunavut', abbreviation: 'NU' }, { name: 'Ontario', abbreviation: 'ON' }, { name: 'Prince Edward Island', abbreviation: 'PE' }, { name: 'Quebec', abbreviation: 'QC' }, { name: 'Saskatchewan', abbreviation: 'SK' }, { name: 'Yukon Territory', abbreviation: 'YT' }],
  AU: [{ name: 'New South Wales', abbreviation: 'NSW' }, { name: 'Victoria', abbreviation: 'VIC' }, { name: 'Queensland', abbreviation: 'QLD' }, { name: 'Tasmania', abbreviation: 'TAS' }, { name: 'South Australia', abbreviation: 'SA' }, { name: 'Western Australia', abbreviation: 'WA' }, { name: 'Northern Territory', abbreviation: 'NT' }, { name: 'Australian Capital Territory', abbreviation: 'ACT' }],
};

const checkoutFields = ['card', 'number', 'name', 'firstName', 'lastName', 'year', 'month', 'cvv', 'security code', 'country', 'zip', 'city', 'address'];

let timeoutId = null;

const IRConfig = {
  ds: {
    script: 'https://d.impactradius-event.com/A2092309-2c19-4fc0-9efa-5955c982d8981.js',
    trackConversionId: 20550,
  },
  fa: {
    script: 'https://utt.impactcdn.com/A2092309-2c19-4fc0-9efa-5955c982d8981.js',
    trackConversionId: 38081,
  },
};

export default (store) => {
  store.registerModule('checkout', {
    namespaced: true,
    state: () => ({
      existingEmail: '',
      chargify: null,
      errors: {},
      loadingChargify: true,
      loading: false,
      paymentInfo: {
        edit: true,
        method: 'card',
        isRestored: false,
        invoice: false,
        card: {
          name: null,
          firstName: null,
          lastName: null,
          country: '',
          zip: null,
          state: '',
          city: null,
          address: null,
          type: null,
        },
        plan: {
          name: 'annual',
          coupon: { value: '', removed: false },
          currency: '',
          quantity: 2,
        },
        securityToken: null,
      },
      account: {
        email: '',
        password: '',
        firstName: '',
        lastName: '',
      },
      order: { },
      currentTab: 'account',
      previewFetched: false,
      countriesStates,
    }),
    mutations: {
      initChargify(state, instance) {
        state.chargify = instance || new window.Chargify();
      },
      setPaymentMethod(state, method) {
        state.paymentInfo.method = method;
      },
      setCardName(state, name) {
        const [firstname = '', lastname = ''] = (name || '').trim().split(' ');

        state.paymentInfo.card.name = name;
        state.paymentInfo.card.firstName = firstname;
        state.paymentInfo.card.lastName = lastname;
      },
      setCountry(state, country) {
        state.paymentInfo.card.country = country;
      },
      setState(state, s) {
        state.paymentInfo.card.state = s;
      },
      setPlan(state, plan) {
        state.paymentInfo.plan.name = plan;
      },
      setCoupon(state, coupon) {
        state.paymentInfo.plan.coupon.value = coupon;
      },
      setCouponRemoved(state, removed) {
        state.paymentInfo.plan.coupon.removed = removed;
      },
      setCurrency(state, currency) {
        state.paymentInfo.plan.currency = currency;
      },
      setQuantity(state, { quantity, input }) {
        if (input) {
          state.paymentInfo.plan.quantity = quantity;
          return;
        }

        const q = state.paymentInfo.plan.quantity + quantity;

        if (q >= (state.order.changeAllocation || state.order.coins ? 1 : 2)) {
          state.paymentInfo.plan.quantity = q;
        }
      },
      setZip(state, zip) {
        state.paymentInfo.card.zip = zip;
      },
      setOrder(state, summary) {
        state.order = summary;
      },
      setCardType(state, type) {
        state.paymentInfo.card.type = type;
      },
      setCurrentTab(state, tab) {
        state.currentTab = tab;
      },
      setChargifyErrors(state, errors) {
        const event = `${this.$router.currentRoute.query.plan}_completepayment.click`;

        if (event) {
          const errorsArr = Object.entries(errors);

          if (errorsArr.length) {
            const [[k, v]] = errorsArr;
            window.$nuxt.$tracking.sendAsyncEvent(event, { status: 'fail', error: v || `${k} field is required` });
          }
        }

        if (Object.keys(errors).length !== 0) {
          clearTimeout(timeoutId);
        }

        state.errors = errors;
        state.loading = false;
      },
      setPaymentInfoRestored(state) {
        state.paymentInfo.isRestored = true;
      },
      setPaymentInfoEdit(state, edit) {
        state.paymentInfo.edit = edit;
      },
      setInvoice(state, invoice) {
        state.paymentInfo.invoice = invoice;
      },
      setCity(state, city) {
        state.paymentInfo.card.city = city;
      },
      setAddress(state, address) {
        state.paymentInfo.card.address = address;
      },
      setLoadingChargify(state, loading) {
        state.loadingChargify = loading;
      },
      setLoading(state, loading) {
        state.loading = loading;
      },
      previewFetched(state, condition) {
        state.previewFetched = condition;
      },
      setAccountEmail(state, email) {
        state.account.email = email;
      },
      setAccountPassword(state, password) {
        state.account.password = password;
      },
      setAccountFirstName(state, firstName) {
        state.account.firstName = firstName;
      },
      setAccountLastName(state, lastName) {
        state.account.lastName = lastName;
      },
      setSecurityToken(state, token) {
        state.paymentInfo.securityToken = token;
      },
      setExistingEmail(state, email) {
        state.existingEmail = email;
      },
    },
    getters: {
      currency(state) {
        const { currency } = state.paymentInfo.plan;

        if (!currency) {
          return false;
        }

        return { sign: currencies[currency], value: currency, text: `${currencies[currency]} ${currency}` };
      },
      currencies() {
        return currencies;
      },
      errors: (state) => (key) => {
        if (Array.isArray(key)) {
          for (let i = 0; i < key.length; i += 1) {
            if (key[i] in state.errors) {
              return { message: state.errors[key[i]], any: true };
            }
          }

          return { message: false, any: false };
        }

        if (key in state.errors) {
          return { message: state.errors[key], any: true };
        }

        return { message: false, any: false };
      },
    },
    actions: {
      setChargifyErrors({ state, commit }, errors) {
        commit('setChargifyErrors', errors);
        if (state.order.paymentInfo && state.order.paymentInfo.paymentProfileId && !state.paymentInfo.edit) {
          store.dispatch('modals/open', { name: 'payment-taking-longer', message: state.errors.card || state.errors.number });
        } else if (errors.other) {
          store.dispatch('modals/open', { name: 'payment-taking-longer', message: state.errors.other });
        }
      },
      async setCurrency({ dispatch }, currency) {
        await dispatch('fetchOrderSummary', { currency });
      },
      async fetchOrderSummary({ state, rootState, commit }, payload = {}) {
        try {
          commit('setLoading', true);
          if (rootState.auth.user) {
            commit('setCurrentTab', 'payment');
          }

          commit('setPlan', payload.plan || this.$router.currentRoute.query.plan);
          let coupon = state.paymentInfo.plan.coupon.value;
          const couponRemoved = state.paymentInfo.plan.coupon.removed;

          const { name: plan, currency, quantity } = state.paymentInfo.plan;
          const {
            country, state: st, zip, city, address,
          } = state.paymentInfo.card;
          const { method, securityToken } = state.paymentInfo;

          if (payload.coupon) {
            coupon = payload.coupon;
          }

          const preview = await this.$axios.$post('/payments/checkout/preview', {
            plan,
            quantity,
            zip,
            country,
            state: st,
            city,
            address,
            currency: payload.currency || currency,
            coupon,
            couponRemoved,
            method,
            rkey: payload.rkey || this.$router.currentRoute.query.rkey,
            page: payload.page || this.$router.currentRoute.query.page,
            promo: payload.promo || this.$router.currentRoute.query.promo,
            securityToken: Boolean(securityToken),
          }, { progress: false });

          if (preview.invalidKey && process.client) {
            return this.$router.push({ path: '/checkout/', query: { plan: 'annual' } });
          }

          if (!preview.preview && process.client) {
            if (preview.type === 'downgrade') {
              this.$router.push({ path: '/pricing/', query: { alert: preview.type } });
              return true;
            }

            if (preview.type === 'extend-no-better-price' || preview.type === 'already-purchased') {
              this.$router.push({ path: '/pricing/', query: { alert: preview.type, currentPlan: this.$router.currentRoute.query.plan } });
              return true;
            }

            if (preview.type === 'course-purchased') {
              this.$router.push({ path: '/pricing/', query: { alert: preview.type } });
              return true;
            }

            if (preview.existingLifetime) {
              this.$router.push('/pricing/?alert=existing-lifetime');
              return true;
            }

            if (preview.lifetime) {
              this.$router.push('/pricing/?alert=lifetime');
              return true;
            }

            if (preview.notChargify) {
              this.$router.push('/pricing/?alert=fs-not-allowed');
              return true;
            }

            if (preview.invalidEmail) {
              this.$router.push('/pricing/?alert=invalid-email');
              return true;
            }

            if (preview.redirect) {
              this.$router.push(`/checkout/?plan=${preview.redirect}`);
              return true;
            }

            window.location.href = '/pricing/';
            return true;
          }

          if (preview) {
            commit('setOrder', preview);

            if (preview.currency) {
              commit('setCurrency', preview.currency);
            }

            if (preview.coupon) {
              commit('setCoupon', preview.coupon.value);
            }

            if (preview.country) {
              commit('setCountry', preview.country);
            }

            if (preview.state) {
              commit('setState', preview.state);
            }

            if (preview.zip) {
              commit('setZip', preview.zip);
            }

            if (preview.city) {
              commit('setCity', preview.city);
            }

            if (preview.address) {
              commit('setAddress', preview.address);
            }

            if (preview.invoice) {
              commit('setInvoice', preview.invoice.userInvoiceActive);
            }

            if (preview.paymentInfo && !state.paymentInfo.isRestored) {
              commit('setPaymentInfoEdit', false);
              if (!state.paymentInfo.card.country) {
                commit('setCountry', preview.paymentInfo.country);
              }

              if (!state.paymentInfo.card.state) {
                commit('setState', preview.paymentInfo.state);
              }

              if (!state.paymentInfo.card.zip) {
                commit('setZip', preview.paymentInfo.zip);
              }

              if (preview.invoice && preview.invoice.userInvoiceActive) {
                if (!state.paymentInfo.card.city) {
                  commit('setCity', preview.invoice.invoiceCity);
                }

                if (!state.paymentInfo.card.address) {
                  commit('setAddress', preview.invoice.invoiceAddress);
                }
              }

              if (preview.paymentInfo.paymentType) {
                commit('setPaymentMethod', preview.paymentInfo.paymentType);
              }

              commit('setPaymentInfoRestored');
            }

            if (preview.securityToken) {
              commit('setSecurityToken', preview.securityToken);
            }

            commit('previewFetched', true);
          }

          return true;
        } catch (e) {
          return false;
        } finally {
          commit('setLoading', false);
        }
      },
      async getChargifyToken({ state, commit }) {
        return new Promise((resolve) => state.chargify.token(
          document.getElementById(`payment-form-${state.paymentInfo.method}`),
          (token) => resolve({ token }),
          ({ invalidFields, errors }) => {
            const errs = {};
            commit('setLoading', false);
            commit('setLoadingChargify', false);

            if (errors) {
              if (!Array.isArray(errors)) {
                const field = checkoutFields.find((f) => {
                  const r = new RegExp(`\\b${f}\\b`);

                  return r.test(errors.replace(/[-/\\^$*+?.()|[\]{}']/g, ''));
                });

                if (field) {
                  errs[field] = errors;
                }
              } else {
                errors.forEach((e) => {
                  const field = checkoutFields.find((f) => {
                    const r = new RegExp(`\\b${f}\\b`);

                    return r.test(e.replace(/[-/\\^$*+?.()|[\]{}']/g, ''));
                  });

                  if (field) {
                    errs[field] = e;
                  } else {
                    let message = e;

                    if (message.includes('security token')) {
                      message = 'You have tried too many cards. Please try again in an hour.';
                    }
                    errs.card = message;
                  }
                });
              }
            }

            if (invalidFields) {
              invalidFields.forEach((e) => {
                errs[e] = '';
              });
            }

            clearTimeout(timeoutId);

            commit('setChargifyErrors', errs);
            return resolve({ errors: errs });
          },
        ));
      },
      async getChargifyTokenAndPay({ state, dispatch, commit }) {
        commit('setChargifyErrors', {});

        if (state.countriesStates[state.paymentInfo.card.country] && !state.paymentInfo.card.state) {
          commit('setChargifyErrors', { state: 'State field is required' });
          return false;
        }

        if (state.paymentInfo.card.city && !/^([a-zA-ZÀ-ž\u0370-\u03FF\u0400-\u04FF]| |-)+$/.test(state.paymentInfo.card.city)) {
          commit('setChargifyErrors', { city: 'Invalid City' });
          return false;
        }

        commit('setLoading', true);
        commit('setLoadingChargify', true);

        timeoutId = setTimeout(async () => {
          await store.dispatch('modals/open', { name: 'payment-taking-longer' });

          commit('setLoading', false);
          commit('setLoadingChargify', false);
        }, 40000);

        // If this is new subscription fetch token and create subscription
        if (state.order.newSubscription) {
          let token;
          if (!(state.order.paymentInfo && state.order.paymentInfo.paymentProfileId) || state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }

          store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 4 });

          return dispatch('createSubscription', token);
        }

        if (state.order.upgrade) {
          let token;
          if (state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }

          store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 4 });

          return dispatch('upgradeSubscription', token);
        }

        if (state.order.extend) {
          let token;
          if (state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }

          store.dispatch('dataLayer/addEvent', { event: 'checkoutStep', value: 4 });

          return dispatch('extendSubscription', token);
        }

        if (state.order.course) {
          let token;
          if (!(state.order.paymentInfo && state.order.paymentInfo.paymentProfileId) || state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }

          return dispatch('purchaseCourse', token);
        }

        if (state.order.lifetime) {
          let token;
          if (!(state.order.paymentInfo && state.order.paymentInfo.paymentProfileId) || state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }

          return dispatch('purchaseLifetime', token);
        }

        if (state.order.business && !state.order.changeAllocation) {
          let token;
          if (!(state.order.paymentInfo && state.order.paymentInfo.paymentProfileId) || state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }

          return dispatch('purchaseBusiness', token);
        }

        if (state.order.business && state.order.changeAllocation) {
          let token;
          if (!(state.order.paymentInfo && state.order.paymentInfo.paymentProfileId) || state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }

          return dispatch('changeBusinessAllocation', token);
        }

        if (state.order.coins && !state.order.changeAllocation) {
          let token;
          if (!(state.order.paymentInfo && state.order.paymentInfo.paymentProfileId) || state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }

          return dispatch('purchaseCoins', token);
        }

        if (state.order.coins && state.order.changeAllocation) {
          let token;
          if (!(state.order.paymentInfo && state.order.paymentInfo.paymentProfileId) || state.paymentInfo.edit) {
            ({ token } = await dispatch('getChargifyToken'));

            if (!token) {
              return false;
            }
          }
          return dispatch('changeCoinAllocation', token);
        }

        return false;
      },
      async createSubscription({
        rootState, state, commit, dispatch,
      }, token) {
        try {
          const { method, plan } = state.paymentInfo;
          const {
            subscribed, errors, actionLink, subscriptionId, customerId,
          } = await this.$axios.$post('/payments/chargify/subscriptions', {
            method,
            plan: {
              ...plan,
              coupon: plan.coupon.value,
              rkey: this.$router.currentRoute.query.rkey,
              promo: this.$router.currentRoute.query.promo,
            },
            token,
          });

          if (subscribed) {
            let additionalParams = null;
            if (rootState?.auth?.user?.email) {
              additionalParams = {
                email: rootState.auth.user.email.trim(),
                sha256_email_address: await sha256(rootState.auth.user.email.trim()),
              };
            }

            store.dispatch('dataLayer/addEvent', { event: 'transaction', value: subscriptionId, additionalParams });
            await dispatch('IRTrackConversion', { subscriptionId, customerId });
            await dispatch('trackPaymentEvent');

            window.location.href = `${this.$config.lmsUrl}/thank-you/?subscribe=true`;
          } else {
            if (actionLink) {
              const redirectUrl = window.location.href;
              window.location.href = `${actionLink}&redirect_url=${encodeURIComponent(redirectUrl)}`;

              return false;
            }
            commit('setLoading', false);
            commit('setLoadingChargify', false);
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          console.log('createSubscription', e);
          return false;
        }
      },
      async upgradeSubscription({ state, commit, dispatch }, token) {
        try {
          const {
            method, plan, card: { city, address },
          } = state.paymentInfo;

          const {
            upgraded, errors, subscriptionId, actionLink,
          } = await this.$axios.$post('/payments/chargify/subscriptions/upgrade', {
            method, plan: { ...plan, coupon: plan.coupon.value, rkey: this.$router.currentRoute.query.rkey }, token, city, address,
          });

          if (upgraded) {
            store.dispatch('dataLayer/addEvent', { event: 'transaction', value: subscriptionId });
            await dispatch('trackPaymentEvent');
            window.location.href = `${this.$config.lmsUrl}/thank-you/`;
          } else {
            if (actionLink) {
              const redirectUrl = window.location.href;
              window.location.href = `${actionLink}&redirect_url=${encodeURIComponent(redirectUrl)}`;

              return false;
            }

            commit('setLoading', false);
            commit('setLoadingChargify', false);
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      },
      async extendSubscription({ state, dispatch }, token) {
        try {
          const {
            method, plan, card: { city, address },
          } = state.paymentInfo;

          const { extended, errors, subscriptionId } = await this.$axios.$post('/payments/chargify/subscriptions/extend', {
            method, plan: { ...plan, coupon: plan.coupon.value }, token, city, address,
          });

          if (extended) {
            store.dispatch('dataLayer/addEvent', { event: 'transaction', value: subscriptionId });
            await dispatch('trackPaymentEvent');
            window.location.href = `${this.$config.lmsUrl}/thank-you/`;
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      },
      async purchaseCourse({ state, commit, dispatch }, token) {
        try {
          const { method, plan } = state.paymentInfo;
          const {
            purchased, name, errors, actionLink, subscriptionId,
          } = await this.$axios.$post('/payments/chargify/course', { method, plan: { ...plan, coupon: plan.coupon.value }, token });

          if (purchased) {
            store.dispatch('dataLayer/addEvent', { event: 'transaction', value: subscriptionId });
            await dispatch('trackPaymentEvent');
            if (store.getters['auth/user'].activeSubscriptionPlan === 2) {
              // fix "Your payment is taking longer than usual" appearing with refreshing page
              window.location.href = `/thank-you?course=${name}`;
            } else {
              const countdown10Minutes = new Date().setMinutes(new Date().getMinutes() + 10);
              localStorage.setItem('purchasedCourse', new Date(countdown10Minutes));
              // fix bug with "Your payment is taking longer than usual" with refreshing page
              window.location.href = `/special-offer/${plan.name}/`;
            }
          } else {
            if (actionLink) {
              const redirectUrl = window.location.href;
              window.location.href = `${actionLink}&redirect_url=${encodeURIComponent(redirectUrl)}`;

              return false;
            }
            commit('setLoading', false);
            commit('setLoadingChargify', false);
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      },
      async purchaseLifetime({ state, commit, dispatch }, token) {
        try {
          const { method, plan } = state.paymentInfo;
          const { purchased, errors, actionLink } = await this.$axios.$post('/payments/chargify/lifetime', { method, plan: { ...plan, coupon: plan.coupon.value }, token });

          if (purchased) {
            await dispatch('trackPaymentEvent');
            window.location.href = `${this.$config.lmsUrl}/thank-you/?subscribe=true`;
          } else {
            if (actionLink) {
              const redirectUrl = window.location.href;
              window.location.href = `${actionLink}&redirect_url=${encodeURIComponent(redirectUrl)}`;

              return false;
            }
            commit('setLoading', false);
            commit('setLoadingChargify', false);
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      },
      async purchaseBusiness({ state, commit, dispatch }, token) {
        try {
          const { method, plan } = state.paymentInfo;
          const {
            address, city, country, state: st, zip,
          } = state.paymentInfo.card;

          const { purchased, errors, actionLink } = await this.$axios.$post('/business/subscription', {
            method,
            plan: { ...plan, coupon: plan.coupon.value },
            billingInfo: {
              address,
              city,
              country,
              state: st,
              zip,
            },
            token,
          });

          if (purchased) {
            await dispatch('trackPaymentEvent');
            window.location.href = `${this.$config.lmsUrl}/thank-you/?business=true`;
          } else {
            if (actionLink) {
              const redirectUrl = window.location.href;
              window.location.href = `${actionLink}&redirect_url=${encodeURIComponent(redirectUrl)}`;

              return false;
            }
            commit('setLoading', false);
            commit('setLoadingChargify', false);
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      },
      async changeBusinessAllocation({ state, commit, dispatch }, token) {
        try {
          const { method, plan } = state.paymentInfo;
          const { purchased, errors, actionLink } = await this.$axios.$post('/business/subscription/allocations', { method, plan: { ...plan, coupon: plan.coupon.value }, token });

          if (purchased) {
            await dispatch('trackPaymentEvent');
            window.location.href = `${this.$config.lmsUrl}/business/?purchased=${plan.quantity}`;
          } else {
            if (actionLink) {
              const redirectUrl = window.location.href;
              window.location.href = `${actionLink}&redirect_url=${encodeURIComponent(redirectUrl)}`;

              return false;
            }
            commit('setLoading', false);
            commit('setLoadingChargify', false);
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      },
      async purchaseCoins({ state, commit, dispatch }, token) {
        try {
          const { method, plan } = state.paymentInfo;
          const {
            address, city, country, state: st, zip,
          } = state.paymentInfo.card;

          const { purchased, errors, actionLink } = await this.$axios.$post('/payments/chargify/coins', {
            method,
            plan: { ...plan, coupon: plan.coupon.value },
            billingInfo: {
              address,
              city,
              country,
              state: st,
              zip,
            },
            token,
          });

          if (purchased) {
            await dispatch('trackPaymentEvent');
            window.location.href = `${this.$config.lmsUrl}/coins/`;
          } else {
            if (actionLink) {
              const redirectUrl = window.location.href;
              window.location.href = `${actionLink}&redirect_url=${encodeURIComponent(redirectUrl)}`;

              return false;
            }
            commit('setLoading', false);
            commit('setLoadingChargify', false);
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      },

      async changeCoinAllocation({ state, commit, dispatch }, token) {
        try {
          const { method, plan } = state.paymentInfo;
          const { purchased, errors, actionLink } = await this.$axios.$post('/payments/chargify/coins/allocations', { method, plan: { ...plan, coupon: plan.coupon.value }, token });

          if (purchased) {
            await dispatch('trackPaymentEvent');
            window.location.href = `${this.$config.lmsUrl}/coins/`;
          } else {
            if (actionLink) {
              const redirectUrl = window.location.href;
              window.location.href = `${actionLink}&redirect_url=${encodeURIComponent(redirectUrl)}`;

              return false;
            }
            commit('setLoading', false);
            commit('setLoadingChargify', false);
          }

          if (errors) {
            dispatch('setChargifyErrors', errors);

            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      },

      async setPaymentCart({ state }, payload = {}) {
        try {
          const paymentInfo = {
            plan: state.paymentInfo.plan.name,
            ...payload,
          };

          if (paymentInfo.email && !window.$nuxt.$validEmail(paymentInfo.email)) {
            return false;
          }

          await this.$axios.$post('/payments/checkout/cart', paymentInfo, { progress: false });
          return true;
        } catch (e) {
          return e;
        }
      },
      async IRTrackConversion({ state }, { subscriptionId, customerId } = {}) {
        const { plan } = state.paymentInfo;
        const { order } = state;

        const config = IRConfig[this.$config.platform];

        // eslint-disable-next-line no-underscore-dangle
        const loadIR = await this._vm.$loadScript(config.script);

        if (loadIR) {
          const trackingObject = {};

          trackingObject.orderId = subscriptionId;
          trackingObject.customerId = customerId;
          trackingObject.currencyCode = plan.currency;
          trackingObject.items = [];

          const price = +order.plan.price;
          let discount = +order.discount;

          if (plan.coupon.value) {
            trackingObject.orderPromoCode = plan.coupon.value;
            discount -= +order.plan.saving.amount;
          }

          trackingObject.items.push({
            subTotal: +(Number(price - discount)).toFixed(2),
            category: 'digital',
            sku: order.plan.name,
            quantity: 1,
          });

          // eslint-disable-next-line no-undef
          ire('trackConversion', config.trackConversionId, trackingObject);
        }
      },
      async trackPaymentEvent() {
        const event = `${this.$router.currentRoute.query.plan}_completepayment.click`;

        window.$nuxt.$tracking.sendAsyncEvent(event, { status: 'success' });
      },
    },
  });

  if (process.client) {
    // Workaround to delay firing this watched when initial state is restored
    setTimeout(() => {
      store.watch(
        ({
          checkout: {
            paymentInfo: {
              card: {
                country, state, zip,
              }, method, plan: { quantity },
            },
          },
        }) => ({
          country, state, zip, method, quantity,
        }),
        debounce((n, o) => {
          if (store.state.checkout.previewFetched && Object.keys(o).some((field) => o[field] !== n[field])) {
            store.dispatch('checkout/fetchOrderSummary');
          }
        }, 500),
        { immediate: false },
      );
    }, 1000);
  }
};
